import { Box, Button } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Flex from "../../../../../../../../components/Flex";
import { ScrollArea } from "../../../../../../../../components/ScrollBar";
import Text from "../../../../../../../../components/Text";
import { useDebounce } from "../../../../../../../../hooks/use-debounce";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useCreatePatientSocialHistory } from "../../../../../../../../queries/history/social-history/use-create-patient-social-history";
import { useGetAllPatientSocialHistory } from "../../../../../../../../queries/history/social-history/use-get-all-patient-social-history";
import { useSocialHistoryQuestions } from "../../../../../../../../queries/history/social-history/use-get-social-history-questions";
import { useUpdatePatientSocialHistory } from "../../../../../../../../queries/history/social-history/use-update-patient-social-history";
import { GetPatientSocialHistorySocialHistoryTypeEnum } from "../../../../../../../../services";
import {
  // DEFAULT_FONTSIZE,
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../../utils/utils";
import { SOCIAL_HISTORY_QUESTION_TYPES_MAPPER } from "../history-constant";
import DynamicForm, { Response } from "./DynamicForm";
import { SOCIAL_HISTORY_COLORS } from "../../../../../../../../utils/util.standardized";

interface Props {
  selectedCard: GetPatientSocialHistorySocialHistoryTypeEnum | string;
  skipFunction?: any;
  currentIndex?: number;
  showSkipButton?: boolean;
  resetForm?: () => void;
  appointmentId?: string;
}

const SelectedQuestionLayout = (props: Props) => {
  const {
    selectedCard,
    skipFunction,
    currentIndex = 0,
    showSkipButton = true,
    resetForm,
    appointmentId = undefined,
  } = props;

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const { patientId } = useParams() as { patientId: any };
  const [pagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting] = useState<GridSortItem[]>([]);
  const [searchKey] = useDebounce<string>("");
  const { mutateAsync: createPatientSocialHistory } =
    useCreatePatientSocialHistory();

  const { mutateAsync: updatePatientSocialHistory } =
    useUpdatePatientSocialHistory();

  const { data: patientSocialHistoryQuestionsList } =
    useGetAllPatientSocialHistory({
      patientUuid: patientId,
      socialHistoryType:
        selectedCard as GetPatientSocialHistorySocialHistoryTypeEnum,
      appointmentId: appointmentId ?? undefined,
      xTENANTID: getXtenantId() ?? "",
    });

  const { data: commonHistoryQuestionsList } = useSocialHistoryQuestions({
    socialHistoryType:
      selectedCard as GetPatientSocialHistorySocialHistoryTypeEnum,
    pagination,
    gridSorting,
    searchString: searchKey,
    xTENANTID: getXtenantId(),
  });
  const [formData, setFormData] = useState<Response[]>([]);

  useEffect(() => {
    setFormData(
      !!patientSocialHistoryQuestionsList?.data?.patientQuestionAnswers?.length
        ? patientSocialHistoryQuestionsList?.data?.patientQuestionAnswers
        : commonHistoryQuestionsList?.data
    );
  }, [patientSocialHistoryQuestionsList, commonHistoryQuestionsList]);

  function submitResponse() {
    const payload = {
      patientQuestionAnswerRequest: {
        patient: {
          [patientId]: `${patientId}`,
        },
        appointmentId: appointmentId ?? undefined,
        socialHistoryType: selectedCard ?? "",
        patientQuestionAnswers: formData,
      },
      xTENANTID: getXtenantId() ?? undefined,
    };
    // console.log("payload ", payload);

    showToastAndReset(
      !!patientSocialHistoryQuestionsList?.data?.patientQuestionAnswers?.length
        ? updatePatientSocialHistory(payload as any)
        : createPatientSocialHistory(payload as any)
    );
  }

  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        resetForm?.();
        setFormData([]);
        queryClient.invalidateQueries({
          queryKey: ["all-patient-social-history"],
        });
        queryClient.invalidateQueries({
          queryKey: ["all-patient-social-history-status"],
        });
        if (appointmentId) {
          queryClient.invalidateQueries({
            queryKey: [
              "all-patient-social-history-by-appointment-id",
              appointmentId,
            ],
          });
        }

        // navigate to the next section
        skipFunction?.(currentIndex);
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Flex
        justifyContent={"space-between"}
        height={"30px"}
        alignItems={"center"}
        flexShrink={0}
        sx={{ backgroundColor: SOCIAL_HISTORY_COLORS.HEADING_BG, px: 2 }}
      >
        <Text fontWeight="500" fontSize={12}>
          {SOCIAL_HISTORY_QUESTION_TYPES_MAPPER[selectedCard ?? ""]}
        </Text>
      </Flex>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          flex: 1,
          minHeight: 0,
        }}
      >
        <ScrollArea sx={{ flex: 1, minHeight: 0 }} height={"auto"}>
          <Box>
            <DynamicForm formData={formData} setFormData={setFormData} />
          </Box>
        </ScrollArea>
        <Box
          sx={{
            padding: "12px",
            display: "flex",
            flexDirection: "row-reverse",
            gap: "10px",
            flexShrink: 0,
            borderTop: `1px solid ${SOCIAL_HISTORY_COLORS.FOOTER_BORDER}`,
            backgroundColor: SOCIAL_HISTORY_COLORS.FOOTER_BG,
          }}
        >
          <Button
            disabled={!selectedCard}
            onClick={() => submitResponse()}
            variant="contained"
          >
            Save Response
          </Button>
          {showSkipButton && (
            <Button
              disabled={!selectedCard}
              onClick={() => skipFunction(currentIndex)}
              variant="outlined"
            >
              Skip
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SelectedQuestionLayout;
